<template>
    <div id="app">
      <h1>户型图生成</h1>
      <div>
        <input v-model="bimjsonUrl" placeholder="输入Bimjson URL" />
        <button @click="goToPlanCheck">户型图生成</button>
        
        <div class="image-container" v-if="imageUrl">
          <!-- 图片加载遮罩 -->
          <div class="loading-overlay" v-if="isProcessing">
            <div class="spinner"></div>
            <p>{{ processingStatus }}</p>
          </div>
          
          <img 
            :src="imageUrl" 
            alt="户型图" 
            @mousemove="handleMouseMove"
            @mouseleave="clearCoordinates"
            @load="onImageLoaded"
            ref="floorplanImage"
            :class="{ 'processing': isProcessing }"
            :style="{ pointerEvents: isDragging ? 'none' : 'auto' }"
          />
          
          <!-- 原点标记点 - 可拖拽 -->
          <div 
            v-if="showOriginMarker" 
            class="origin-marker" 
            :style="{ left: originPoint.x + 'px', top: originPoint.y + 'px' }"
            @mousedown="startDrag"
            :class="{ 'dragging': isDragging }"
          >
            <div class="axis-line x-axis" v-if="!isConfirmed"></div>
            <div class="axis-line y-axis" v-if="!isConfirmed"></div>
          </div>
          
          <!-- 确认按钮 -->
          <div 
            v-if="showOriginMarker && !isConfirmed" 
            class="confirm-button"
            :style="{ left: originPoint.x + 20 + 'px', top: originPoint.y - 20 + 'px' }"
          >
            <button @click="confirmOrigin">确认基准点</button>
          </div>
          
          <div 
            class="coordinates" 
            v-if="showCoordinates && axisCalibrated"
            :style="{ left: coordLeft + 'px', top: coordTop + 'px' }"
          >
            X: {{ actualX.toFixed(1) }} cm, Y: {{ actualY.toFixed(1) }} cm
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'PlanCheck',
    data() {
      return {
        bimjsonUrl: '',
        imageUrl: null,
        // 图片尺寸信息
        originalWidth: 0,
        originalHeight: 0,
        displayWidth: 0,
        displayHeight: 0,
        // 坐标数据
        actualX: 0,
        actualY: 0,
        showCoordinates: false,
        coordLeft: 0,
        coordTop: 0,
        // 坐标轴校准数据
        axisCalibrated: false,
        originPoint: { x: 0, y: 0 }, // 原点坐标（图片上的像素位置）
        pixelsPerCm: 0.5, // 像素与厘米的换算比例，可根据实际情况调整
        showOriginMarker: false, // 是否显示原点标记
        isConfirmed: false, // 是否已确认原点位置
        isDragging: false, // 是否正在拖拽原点
        dragOffset: { x: 0, y: 0 }, // 拖拽偏移量
        // 处理状态
        isProcessing: false,
        processingStatus: ''
      };
    },
    methods: {
      async goToPlanCheck() {
        try {
          this.isProcessing = true;
          this.processingStatus = '正在生成户型图...';
          
          const response = await fetch(`https://plancheck.loca.lt/generate-floorplan`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ url: this.bimjsonUrl })
          });
          
          if (response.ok) {
            const result = await response.json();
            // 将base64数据转换为图片URL
            this.imageUrl = `data:image/png;base64,${result.image_data}`;
            // 重置校准状态
            this.resetCalibration();
          } else {
            const errorData = await response.json();
            alert(`生成户型图失败: ${errorData.error}`);
          }
        } catch (error) {
          console.error('生成户型图时出错:', error);
          alert('生成户型图时发生错误，请检查控制台');
        } finally {
          this.isProcessing = false;
        }
      },
      
      onImageLoaded() {
        const image = this.$refs.floorplanImage;
        if (image) {
          this.originalWidth = image.naturalWidth;
          this.originalHeight = image.naturalHeight;
          this.displayWidth = image.offsetWidth;
          this.displayHeight = image.offsetHeight;
          
          // 提示用户选择原点
          this.processingStatus = '请点击图片设置坐标原点';
          this.isProcessing = false;
          
          // 添加点击事件用于标记原点
          image.addEventListener('click', this.handleOriginClick);
        }
      },
      
      handleOriginClick(event) {
        if (this.axisCalibrated || this.showOriginMarker) return;
        
        const image = this.$refs.floorplanImage;
        const rect = image.getBoundingClientRect();
        
        // 计算点击在图片上的相对位置（像素坐标）
        this.originPoint.x = event.clientX - rect.left;
        this.originPoint.y = event.clientY - rect.top;
        
        // 显示原点标记，但未确认
        this.showOriginMarker = true;
        this.isConfirmed = false;
      },
      
      // 开始拖拽原点 - 修复偏移计算问题
      startDrag(event) {
        event.stopPropagation();
        this.isDragging = true;
        
        const image = this.$refs.floorplanImage;
        const rect = image.getBoundingClientRect();
        
        // 关键修复：计算鼠标相对于图片的位置，再计算与原点的偏移
        const mouseXInImage = event.clientX - rect.left;
        const mouseYInImage = event.clientY - rect.top;
        
        // 计算鼠标相对于原点的偏移量（基于图片坐标系）
        this.dragOffset.x = mouseXInImage - this.originPoint.x;
        this.dragOffset.y = mouseYInImage - this.originPoint.y;
        
        // 添加鼠标移动和释放事件监听
        document.addEventListener('mousemove', this.handleDrag);
        document.addEventListener('mouseup', this.stopDrag);
      },
      
      // 处理拖拽 - 确保与鼠标同步
      handleDrag(event) {
        if (!this.isDragging) return;
        
        const image = this.$refs.floorplanImage;
        const rect = image.getBoundingClientRect();
        
        // 计算鼠标在图片上的相对位置
        const mouseXInImage = event.clientX - rect.left;
        const mouseYInImage = event.clientY - rect.top;
        
        // 基于偏移量计算新的原点位置
        let newX = mouseXInImage - this.dragOffset.x;
        let newY = mouseYInImage - this.dragOffset.y;
        
        // 限制在图片范围内
        newX = Math.max(0, Math.min(newX, rect.width));
        newY = Math.max(0, Math.min(newY, rect.height));
        
        this.originPoint.x = newX;
        this.originPoint.y = newY;
      },
      
      // 停止拖拽
      stopDrag() {
        this.isDragging = false;
        // 移除事件监听
        document.removeEventListener('mousemove', this.handleDrag);
        document.removeEventListener('mouseup', this.stopDrag);
      },
      
      // 确认原点位置
      confirmOrigin() {
        this.isConfirmed = true;
        this.axisCalibrated = true;
        
        // 移除图片点击事件，防止再次选择
        const image = this.$refs.floorplanImage;
        if (image) {
          image.removeEventListener('click', this.handleOriginClick);
        }
      },
      
      resetCalibration() {
        const image = this.$refs.floorplanImage;
        if (image) {
          image.removeEventListener('click', this.handleOriginClick);
          // 如果图片已加载，重新添加点击事件
          if (this.imageUrl) {
            image.addEventListener('click', this.handleOriginClick);
          }
        }
        
        // 重置所有校准相关状态
        this.axisCalibrated = false;
        this.showOriginMarker = false;
        this.isConfirmed = false;
        this.originPoint = { x: 0, y: 0 };
        this.isDragging = false;
      },
      
      handleMouseMove(event) {
        if (!this.axisCalibrated) return;
        
        const image = this.$refs.floorplanImage;
        if (!image) return;
        
        // 获取图片在页面中的位置
        const rect = image.getBoundingClientRect();
        
        // 计算鼠标在显示图片上的相对坐标（像素）
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // 计算实际坐标（厘米）
        // X轴：原点右侧为正方向
        this.actualX = (x - this.originPoint.x) / this.pixelsPerCm;
        // Y轴：原点下方为正方向
        this.actualY = (y - this.originPoint.y) / this.pixelsPerCm;
        
        // 设置坐标显示框位置
        this.coordLeft = x + 10;
        this.coordTop = y + 10;
        
        this.showCoordinates = true;
      },
      
      clearCoordinates() {
        this.showCoordinates = false;
      }
    },
    beforeDestroy() {
      // 清理事件监听
      const image = this.$refs.floorplanImage;
      if (image) {
        image.removeEventListener('click', this.handleOriginClick);
      }
      document.removeEventListener('mousemove', this.handleDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    }
  };
  </script>
  
  <style scoped>
  .image-container {
    position: relative;
    display: inline-block;
    margin-top: 15px;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border: 1px solid #ddd;
    padding: 5px;
    transition: filter 0.3s;
  }
  
  img.processing {
    filter: blur(2px);
  }
  
  .coordinates {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    white-space: nowrap;
    z-index: 10;
  }
  
  /* 原点标记和坐标轴样式 */
  .origin-marker {
    position: absolute;
    width: 12px;
    height: 12px;
    background-color: red;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
    cursor: move;
    transition: all 0.2s;
  }
  
  .origin-marker.dragging {
    transform: translate(-50%, -50%) scale(1.3);
    box-shadow: 0 0 10px rgba(255, 0, 0, 0.6);
  }
  
  .axis-line {
    position: absolute;
    background-color: red;
    transform-origin: left top;
    pointer-events: none;
  }
  
  .x-axis {
    width: 2000px; /* 足够长的线 */
    height: 1px;
    top: 50%;
    left: 50%;
  }
  
  .y-axis {
    width: 1px;
    height: 2000px; /* 足够长的线 */
    top: 50%;
    left: 50%;
  }
  
  .confirm-button {
    position: absolute;
    z-index: 6;
  }
  
  .confirm-button button {
    background-color: #42b983;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 12px;
    cursor: pointer;
  }
  
  .confirm-button button:hover {
    background-color: #359e75;
  }
  
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 20;
  }
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #42b983;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 10px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .calibration-controls, .calibration-info {
    margin-top: 15px;
    padding: 10px;
    background-color: #f5f5f5;
    border-radius: 4px;
    max-width: 600px;
  }
  
  .calibration-controls p, .calibration-info p {
    margin: 5px 0;
    font-size: 14px;
  }
  
  input {
    padding: 6px 10px;
    margin-right: 10px;
    width: 300px;
  }
  
  button {
    padding: 6px 15px;
    background-color: #42b983;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-right: 5px;
    margin-top: 5px;
  }
  
  button:hover {
    background-color: #359e75;
  }
  </style>
    